import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircle2, XCircle, AlertTriangle, Clock, Loader2,
  FileSearch, MapPin, User, Building2, FileText,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  conforme: { Icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50', ring: 'ring-emerald-200', title: 'CONFORME', subtitle: 'Les documents sont authentiques et conformes.' },
  non_conforme: { Icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', ring: 'ring-red-200', title: 'NON CONFORME', subtitle: 'Des irrégularités ont été identifiées.' },
  en_cours: { Icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50', ring: 'ring-blue-200', title: 'VÉRIFICATION EN COURS', subtitle: 'Le dossier est en cours d\'examen.' },
  en_attente: { Icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50', ring: 'ring-amber-200', title: 'EN ATTENTE', subtitle: 'La vérification n\'a pas encore débuté.' },
};

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
    <Icon className="w-4 h-4 text-[#D4A745] mt-0.5 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-sm font-medium text-gray-900 break-words">{value || '—'}</p>
    </div>
  </div>
);

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return iso.slice(0, 10);
  }
};

const VerifyParcel = () => {
  const { id } = useParams();
  const [state, setState] = useState({ loading: true, rec: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/public/parcel-verifications/verify/${encodeURIComponent(id)}`);
        if (!cancelled) setState({ loading: false, rec: res.data, error: null });
      } catch (e) {
        if (!cancelled) setState({
          loading: false,
          rec: null,
          error: e.response?.data?.detail || 'Rapport introuvable',
        });
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-amber-50">
        <Loader2 className="w-10 h-10 animate-spin text-[#D4A745]" />
      </div>
    );
  }

  if (state.error || !state.rec) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-amber-50 px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <XCircle className="w-16 h-16 mx-auto text-red-500 mb-3" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Rapport introuvable</h1>
          <p className="text-gray-600 text-sm">{state.error || 'Cet identifiant de vérification ne correspond à aucun rapport.'}</p>
        </div>
      </div>
    );
  }

  const r = state.rec;
  const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.en_attente;
  const { Icon } = cfg;
  const agency = r.agency || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50 py-10 px-4" data-testid="verify-parcel-page">
      <div className="max-w-2xl mx-auto">
        {/* Agency header */}
        <div className="text-center mb-6">
          {agency.logo_url && (
            <img
              src={agency.logo_url.startsWith('http') ? agency.logo_url : `${API}${agency.logo_url}`}
              alt={agency.name}
              className="w-16 h-16 mx-auto mb-2 rounded-lg shadow object-contain bg-white p-1"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <h2 className="text-lg font-bold text-[#1B3A4B]">{agency.name || "Vérification de parcelle"}</h2>
          {agency.city && <p className="text-xs text-gray-500">{agency.city}{agency.country ? `, ${agency.country}` : ''}</p>}
        </div>

        {/* Verdict card */}
        <div className={`rounded-2xl shadow-xl p-8 text-center ${cfg.bg} ring-1 ${cfg.ring} mb-6`} data-testid="verdict-card">
          <Icon className={`w-20 h-20 mx-auto mb-3 ${cfg.color}`} />
          <h1 className={`text-2xl font-bold ${cfg.color}`}>{cfg.title}</h1>
          <p className="text-sm text-gray-700 mt-2">{cfg.subtitle}</p>
        </div>

        {/* Details card */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileSearch className="w-5 h-5 text-[#D4A745]" />
            <h3 className="text-base font-semibold text-[#1B3A4B]">Détails du rapport</h3>
          </div>

          <Row icon={FileText} label="Référence du rapport" value={r.reference} />
          <Row icon={User} label="Demandeur" value={r.requester_name} />
          <Row icon={MapPin} label="Localisation de la parcelle" value={r.location} />
          {r.district && <Row icon={MapPin} label="Quartier / Cercle" value={r.district} />}
          {r.parcel_reference && <Row icon={Building2} label="Référence parcelle" value={r.parcel_reference} />}
          {r.owner_claimed_name && <Row icon={User} label="Propriétaire déclaré" value={r.owner_claimed_name} />}
          <Row icon={FileText} label="Type de document" value={r.document_type_label} />
          {r.document_number && <Row icon={FileText} label="N° du document" value={r.document_number} />}
          {r.verified_at && <Row icon={CheckCircle2} label="Vérifié le" value={formatDate(r.verified_at)} />}
          {!r.verified_at && r.created_at && <Row icon={Clock} label="Créé le" value={formatDate(r.created_at)} />}

          {r.findings && (
            <div className={`mt-4 p-3 rounded-lg border ${r.status === 'non_conforme' ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50'}`}>
              <div className={`text-[11px] uppercase font-semibold mb-1 ${r.status === 'non_conforme' ? 'text-red-700' : 'text-emerald-700'}`}>
                Conclusion du vérificateur
              </div>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{r.findings}</p>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-500 mt-6">
          Document vérifié officiellement par {agency.name || "l'agence émettrice"}.<br />
          Toute reproduction ou falsification est passible de poursuites.
        </p>
      </div>
    </div>
  );
};

export default VerifyParcel;
